/** Błąd sieci lub serwera (5xx) — operację można bezpiecznie ponowić, bo komendy są idempotentne. */
export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NetworkError";
  }
}

/** Odpowiedź 4xx — serwer świadomie odmówił; ponawianie nic nie zmieni. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: any,
  ) {
    super(body?.message ?? `HTTP ${status}`);
    this.name = "ApiError";
  }
}

export interface Credentials {
  deviceToken: string | null;
  sessionToken: string | null;
}

export class Api {
  constructor(
    readonly baseUrl: string,
    private readonly creds: () => Credentials,
    private readonly timeoutMs = 10_000,
  ) {}

  async request<T>(method: string, path: string, body?: unknown, sessionOverride?: string | null): Promise<T> {
    const { deviceToken, sessionToken: current } = this.creds();
    const sessionToken = sessionOverride ?? current;
    const headers: Record<string, string> = {};
    if (body !== undefined) headers["content-type"] = "application/json";
    if (deviceToken) headers.authorization = `Bearer ${deviceToken}`;
    if (sessionToken) headers["x-session"] = sessionToken;
    let res: Response;
    try {
      res = await fetch(this.baseUrl + path, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: AbortSignal.timeout(this.timeoutMs),
        cache: "no-store",
      });
    } catch (e) {
      throw new NetworkError(e instanceof Error ? e.message : "Brak połączenia");
    }
    const data = await res.json().catch(() => null);
    if (res.status >= 500) throw new NetworkError(`Serwer: HTTP ${res.status}`);
    if (!res.ok) throw new ApiError(res.status, data);
    return data as T;
  }

  get = <T>(path: string) => this.request<T>("GET", path);
  post = <T>(path: string, body: unknown, sessionOverride?: string | null) => this.request<T>("POST", path, body, sessionOverride);

  wsUrl(path: string): string {
    const base = this.baseUrl || location.origin;
    return base.replace(/^http/, "ws") + path;
  }
}
